/**
 * Format Action Summary
 *
 * Transforms raw tool calls into human-readable action summaries
 * for display in the inline proposal component.
 */

import type { ProposedAction } from "../../api/types";

/** Format a tool name into a human-readable label */
export function formatToolName(toolName: string): string {
  return toolName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Get a concise, human-readable summary of what an action does */
export function formatActionSummary(action: ProposedAction): string {
  const params = action.user_modification || action.parameters;

  switch (action.tool_name) {
    case "create_work_item":
      return `Create "${truncate(String(params.title ?? ""), 40)}"`;

    case "change_work_item_status":
      if (params.title) {
        return `Move "${truncate(String(params.title), 30)}" → ${String(params.status_name ?? "new status")}`;
      }
      return `Change status${params.work_item_id ? ` of ${truncateId(String(params.work_item_id))}` : ""}`;

    case "update_work_item": {
      const fields = Object.keys(params).filter(
        (k) => k !== "work_item_id" && k !== "expected_updated_at",
      );
      const label = params.title
        ? `"${truncate(String(params.title), 30)}"`
        : params.work_item_id
          ? truncateId(String(params.work_item_id))
          : "item";
      if (fields.includes("priority")) {
        return `Set priority to ${String(params.priority)} on ${label}`;
      }
      if (fields.includes("description")) {
        return `Update description of ${label}`;
      }
      if (fields.length === 1) {
        return `Update ${fields[0].replace(/_/g, " ")} of ${label}`;
      }
      return `Update ${label}`;
    }

    case "delete_work_item":
      if (params.title) {
        return `Delete "${truncate(String(params.title), 40)}"`;
      }
      return `Delete ${params.work_item_id ? truncateId(String(params.work_item_id)) : "item"}`;

    case "set_parent":
      return `Set parent for ${params.title ? `"${truncate(String(params.title), 30)}"` : "item"}`;

    case "assign_work_item":
      return `Assign ${params.title ? `"${truncate(String(params.title), 30)}"` : "item"}`;

    default:
      return formatToolName(action.tool_name);
  }
}

/** Truncate a string to a max length, adding ellipsis if needed */
function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 1) + "\u2026";
}

/** Show first 8 chars of an ID for display */
function truncateId(id: string): string {
  return id.slice(0, 8) + "\u2026";
}
